import { useDispatch, useSelector } from "react-redux";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import { useState } from "react";
import { type ClusterGroup, type ClusterItem } from "~/features/selectionAssistant/bearing/types";
import { type RootState } from "~/app/store";
import { useOnMount } from "~/common/hooks/useOnMount";
import { getTileDisplayData } from "~/features/selectionAssistant/bearing/api/getTileDisplayData";
import { type LoadableEntry } from "~/app/store/loadableTypes";
import Spinner from "~/common/components/Spinner/Spinner";
import SimpleExternalContent from "~/features/configurator/Linear/components/SimpleExternalContent";
import { updateAssistantValues } from "~/features/search/redux/actions";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { selectAssistantValues, selectFacetValueByCode } from "~/features/search/redux/searchSelectors";
import { styled } from "~/common/mui";

const CharacteristicList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& > * + *": {
    marginTop: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-s")]: {
    flexDirection: "row",
    flexWrap: "wrap",
    margin: `-${theme.spacing(2)} -${theme.spacing(1)}`,
    [theme.breakpoints.up("desktop-m")]: {
      margin: `-30px -${theme.spacing(2)}`,
    },
    "& > *": {
      flex: "1 0 40%",
      margin: `${theme.spacing(2)} ${theme.spacing(1)}`,
      [theme.breakpoints.up("desktop-m")]: {
        margin: `30px ${theme.spacing(2)}`,
      },
    },
  },
}));

const CharacteristicTitle = styled("div")(({ theme }) => ({
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(2.5),
  },
}));

const TileContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
  [`@media (min-width: ${theme.breakpoints.values.tablet}px) and (max-width: ${theme.breakpoints.values["desktop-s"] - 1}px)`]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gap: "30px",
  },
}));

const Tile = styled(Card)(({ theme }) => ({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("desktop-s")]: {
    padding: `0 ${theme.spacing(2)} 0 0`,
    flexDirection: "row",
  },
}));

const TileSvg = styled("img")(({ theme }) => ({
  width: "50px",
  height: "50px",
  flex: "none",
  [theme.breakpoints.up("desktop-s")]: {
    width: "58px",
    height: "58px",
    marginBottom: 0,
    marginRight: theme.spacing(2),
    flex: "none",
  },
}));

const TileText = styled("p")(({ theme }) => ({
  margin: `${theme.spacing(0.5)} 0 0`,
  [theme.breakpoints.up("desktop-s")]: {
    padding: "11px 0",
    margin: "0 auto 0 0",
    display: "flex",
    alignItems: "center",
  },
}));

const TileCheck = styled(CheckedCircle)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1.5),
  top: theme.spacing(1.5),
  [theme.breakpoints.up("desktop-s")]: {
    position: "initial",
    alignSelf: "center",
    marginLeft: theme.spacing(2),
    flex: "none",
  },
}));

const useCharacteristicData = () => {
  const [data, setData] = useState<LoadableEntry<ClusterGroup[]>>({ state: "loading" });
  useOnMount(() => {
    getTileDisplayData()
      .then((data) => setData({ state: "loaded", data }))
      .catch(() => setData({ state: "error" }));
  });

  return data;
};

export const CharacteristicsFilter = () => {
  const data = useCharacteristicData();
  const { t } = useTranslation();

  if (data.state === "loading") {
    return <Spinner />;
  }

  if (data.state === "error") {
    return <SimpleExternalContent.DefaultErrorMsg />;
  }

  const characteristics = data.data!;

  return (
    <CharacteristicList>
      {characteristics.map((characteristic) => (
        <div key={characteristic.code}>
          <CharacteristicTitle>
            <span>{t(characteristic.name)}</span>
            <LinearInfo
              className={"icon-margin-l"}
              info={characteristic.description}
              popupClassName={"scha__lc__external-content--shaft"}
            />
          </CharacteristicTitle>
          <TileContainer>
            {characteristic.clusterItems.map((tile) => (
              <CharacteristicTile key={tile.code} item={tile} />
            ))}
          </TileContainer>
        </div>
      ))}
    </CharacteristicList>
  );
};

const useSelectedState = (code: string) => {
  const dispatch = useDispatch();
  const isSelected = useSelector((state: RootState) => {
    const assistantValues = selectAssistantValues(state);
    return assistantValues.includes(code);
  });

  const toggle = () => dispatch(updateAssistantValues(code));

  return [isSelected, toggle] as const;
};

const selectIsEnabled = (code: string) => (state: RootState) => {
  const facetValue = selectFacetValueByCode(state, code);
  return !!facetValue?.values.find((facetValue) => facetValue.code === "true" && facetValue.count > 0);
};

const CharacteristicTile = ({ item }: { item: ClusterItem }) => {
  const [selected, toggleSelected] = useSelectedState(item.code);
  const enabled = useSelector(selectIsEnabled(item.code));

  const { t } = useTranslation();

  return (
    <Tile
      innerClassName={"scha__sa__tile__inner"}
      data-test-id={`SATile-${item.code}`}
      active={selected}
      onClick={toggleSelected}
      disabled={!enabled && !selected}
    >
      <TileSvg src={selected ? item.activeImageUrl : item.imageUrl} alt="" />
      <TileText>{t(item.text)}</TileText>
      <TileCheck checked={selected} />
    </Tile>
  );
};
